/**
 * Egress codec deny-list (frontend mirror).
 * MUST stay in sync with bridge/encoder/pkg/driver/common/codec/codec_support.go
 *
 * The two warnings are composed by template, not shared as a literal, so the
 * rendered strings are pinned by that package's testdata/egress_messages.json
 * and asserted from both languages.
 *
 * Backend uses MimeType constants from common.newts/common:
 *   MimeTypeH264 = "video/H264"
 *   MimeTypeH265 = "video/H265"
 */

// cspell:ignore testdata
export type EgressFeature = 'recording' | 'rtmp' | 'ndi';

// A deny-list, not an allow-list: an allow-list rejects every codec nobody
// thought to enumerate, and Studio pins NDI ingress to VP8, so an H264-only
// allow-list warned on every NDI source. LiveKit egress transcodes the rest.
const DENIED_EGRESS_CODECS = new Set(['video/H265']);

const FEATURE_DISPLAY: Record<EgressFeature, string> = {
  recording: 'Recording',
  rtmp: 'RTMP streaming',
  ndi: 'NDI output'
};

/**
 * MIME subtype for display (`video/H265` -> `H265`). Undefined when the codec
 * is unknown, so callers can fall back to their own "n/a" marker. Shared by the
 * warning template below and the monitoring summary's Codec cell so the two
 * never render the same codec differently.
 */
export const codecDisplayName = (mime?: string | null): string | undefined => {
  if (!mime) return undefined;
  return mime.split('/')[1] ?? mime;
};

/**
 * Returns a user-facing warning if the source codec cannot be carried by this
 * egress feature. Returns undefined when codec is unknown (null/undefined/empty)
 * or allowed -- the Layer 1 backend safety net handles unknown codecs at start time.
 * Per rule: absent-data-gates-permissively (ai.knowledge/global) -- an older
 * bridge that omits the codec is never warned.
 */
export const getCodecWarning = (
  feature: EgressFeature,
  codec: string | undefined | null
): string | undefined => {
  if (codec === undefined || codec === null || codec === '') return undefined;
  if (!DENIED_EGRESS_CODECS.has(codec)) return undefined;
  const displayName = codecDisplayName(codec) ?? codec;
  return `${FEATURE_DISPLAY[feature]} doesn't support ${displayName} sources. Switch the camera's stream to H264.`;
};

/**
 * Short reason for a picker OPTION whose source this feature cannot carry
 * (`H265 not supported`). Composed from the same `codecDisplayName` the long
 * warning uses, so a dropdown entry and the card error can never name the
 * codec differently -- this module stays the single TS source of the wording.
 * Returns undefined when the codec is allowed or unknown, matching
 * `getCodecWarning`.
 */
export const getCodecOptionSuffix = (
  feature: EgressFeature,
  codec: string | undefined | null
): string | undefined => {
  // Delegate the allow/deny decision rather than re-testing the deny-list:
  // one gate, so an option can never disagree with the warning beside it.
  if (!getCodecWarning(feature, codec)) return undefined;
  return `${codecDisplayName(codec) ?? codec} not supported`;
};

/**
 * Full label for a picker option: the source name, suffixed with the reason
 * when this feature cannot carry its codec. Callers render this verbatim so
 * the suffix is never re-spelled at the call site.
 */
export const getCodecOptionLabel = (
  feature: EgressFeature,
  name: string,
  codec: string | undefined | null
): string => {
  const suffix = getCodecOptionSuffix(feature, codec);
  return suffix === undefined ? name : `${name} — ${suffix}`;
};

/**
 * Deterministic notification id for the "this source's codec is refused" toast.
 * `post()` otherwise generates a random id, and the notice renders its id as
 * the STag uniqueID -- pinning it is what lets a HIL spec address the toast by
 * tag (`data:notifications.notice:codec-blocked-ndi-<deviceId>`) instead of
 * matching on its copy. Lower-cased because STagNew lower-cases uniqueIDs.
 * Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
 */
export const codecBlockedNoticeId = (
  feature: EgressFeature,
  deviceId: import('@studio/models/device').Device['id']
): string => `codec-blocked-${feature}-${deviceId}`.toLowerCase();
